// Amici.tsx — v033: /amici. Le richieste arrivate (Accetta / No grazie), gli amici con il pallino verde quando sono
// online e «Sfida» quando si possono sfidare (online e accettano sfide), le richieste mandate. Si aggiorna da sola ogni
// AGGIORNA_MS mentre è aperta. Si diventa amici a fine partita («Aggiungi agli amici») o dal link personale (@nome).
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Palco } from "../componenti/Palco.tsx";
import { Indietro } from "../componenti/Indietro.tsx";
import { Finestra } from "../componenti/Finestra.tsx";
import { useAccount } from "../servizi/account.ts";
import {
  accettaAmicizia,
  chiediAmiciziaInvitante,
  impostaRicevute,
  leggiAmici,
  leggiInvitante,
  leggiSettimanaAmici,
  rifiutaAmicizia,
  bloccaPersona,
  sfidaAmico,
  togliAmico,
  type Amico,
  type ElencoAmici,
  type Invitante,
  type RigaSettimana,
} from "../servizi/amici.ts";
import { Avatar } from "../componenti/Avatar.tsx";
import { vai } from "../servizi/percorso.ts";
import { vibra } from "../servizi/nativo.ts";
import { PERCORSO_ACCESSO, PERCORSO_DISPONIBILI } from "../costanti.ts";
import { ErroreSfida, IconaSfida, RigaPersona, usaSfidaPersona } from "./Persone.tsx";
import { PersoneBloccate } from "./PersoneBloccate.tsx";

const AGGIORNA_MS = 20_000;

export function Amici({ onIndietro }: { onIndietro: () => void }) {
  const { t } = useTranslation();
  const account = useAccount();
  const [elenco, setElenco] = useState<ElencoAmici | null>(null);
  const [errore, setErrore] = useState(false);
  const [togli, setTogli] = useState<Amico | null>(null);
  const [bloccati, setBloccati] = useState(0); // v039: ricarica l'elenco dei bloccati dopo un blocco
  const { sfida, invio, errore: erroreSfida } = usaSfidaPersona();
  const registrato = account.tipo === "registrato";
  // v037: chi mi ha portato qui («aggiungilo con un tocco») e la classifica della settimana tra amici
  const [invitante, setInvitante] = useState<Invitante | null>(null);
  const [invitanteChiesto, setInvitanteChiesto] = useState(false);
  const [settimana, setSettimana] = useState<RigaSettimana[] | null>(null);
  useEffect(() => {
    if (!registrato) return;
    void leggiInvitante()
      .then((r) => setInvitante(r.invitante))
      .catch(() => undefined);
    void leggiSettimanaAmici()
      .then((r) => setSettimana(r.classifica))
      .catch(() => undefined);
  }, [registrato]);

  const carica = useCallback(async () => {
    try {
      const e = await leggiAmici();
      setElenco(e);
      setErrore(false);
      impostaRicevute(e.ricevute.length);
    } catch {
      setErrore(true);
    }
  }, []);
  useEffect(() => {
    if (!registrato) return;
    void carica();
    const id = setInterval(() => document.visibilityState === "visible" && void carica(), AGGIORNA_MS);
    return () => clearInterval(id);
  }, [registrato, carica]);

  const rispondi = async (id: number, si: boolean) => {
    vibra(si ? "successo" : "leggero");
    try {
      await (si ? accettaAmicizia(id) : rifiutaAmicizia(id));
    } finally {
      await carica();
    }
  };

  const online = elenco?.amici.filter((a) => a.online).length ?? 0;
  return (
    <div className="pagina pagina-account ld-pagina">
      <header className="ld-barra">
        <Indietro onClick={onIndietro} />
        <span />
      </header>
      <main className="account-centro ld-amici">
        <Palco mascotte="caso" colore="primario" titolo={t("amici.titolo")} sottotitolo={elenco && elenco.amici.length > 0 ? t("amici.online_ora", { n: online }) : t("amici.sotto")} />
        {!registrato ? (
          <section className="scheda-account">
            <p>{t("amici.serve_account")}</p>
            <button type="button" className="pulsante pulsante-primario" onClick={() => vai(PERCORSO_ACCESSO)}>
              {t("account.accedi")}
            </button>
          </section>
        ) : (
          <>
            {errore && !elenco && <p className="nota" role="alert">{t("account.errore_generico")}</p>}
            {!elenco && !errore && <p className="nota" aria-busy="true">{t("generale.caricamento")}</p>}
            <ErroreSfida codice={erroreSfida} />

            {invitante && invitante.stato !== "amici" && (
              <section className="scheda-account ld-amici-invitante" aria-live="polite">
                <div className="ld-amici-invitante-chi">
                  <Avatar quale={invitante.avatar ?? "mistero"} dimensione={56} anello />
                  <p>{t("amici.invitante", { nome: invitante.nome })}</p>
                </div>
                {invitanteChiesto || invitante.stato === "inviata" ? (
                  <p className="nota-centrata">{t("amici.invitante_chiesto", { nome: invitante.nome })}</p>
                ) : (
                  <button
                    type="button"
                    className="pulsante pulsante-primario"
                    onClick={() => {
                      vibra("successo");
                      void chiediAmiciziaInvitante()
                        .then(() => setInvitanteChiesto(true))
                        .finally(() => void carica());
                    }}
                  >
                    {invitante.stato === "ricevuta" ? t("amici.accetta") : t("amici.invitante_aggiungi", { nome: invitante.nome })}
                  </button>
                )}
              </section>
            )}

            {settimana && settimana.length > 1 && (
              <section className="scheda-account ld-amici-settimana" aria-labelledby="amici-settimana">
                <h2 className="scheda-titolo" id="amici-settimana">
                  {t("amici.settimana")}
                </h2>
                <ol className="ld-settimana">
                  {settimana.slice(0, 10).map((r, i) => (
                    <li key={r.id} data-io={r.io} data-posto={i + 1}>
                      <span className="ld-settimana-posto">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}</span>
                      <Avatar quale={r.avatar ?? "mistero"} dimensione={36} />
                      <span className="ld-settimana-nome">{r.io ? t("amici.tu") : r.nome}</span>
                      <span className="ld-settimana-punti">{t("amici.settimana_vinte", { n: r.vinte, partite: r.partite })}</span>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {elenco && elenco.ricevute.length > 0 && (
              <section className="scheda-account ld-amici-ricevute" aria-labelledby="amici-ricevute">
                <h2 className="scheda-titolo" id="amici-ricevute">
                  {t("amici.ricevute", { n: elenco.ricevute.length })}
                </h2>
                <ul className="ld-persone">
                  {elenco.ricevute.map((p) => (
                    <RigaPersona key={p.id} nome={p.nome} nomeUtente={p.nomeUtente} avatar={p.avatar} stato={t("amici.vuole_amicizia")}>
                      <button type="button" className="pulsante pulsante-primario pulsante-piccolo" onClick={() => void rispondi(p.id, true)}>
                        {t("amici.accetta")}
                      </button>
                      <button type="button" className="pulsante pulsante-piccolo" onClick={() => void rispondi(p.id, false)}>
                        {t("amici.no_grazie")}
                      </button>
                    </RigaPersona>
                  ))}
                </ul>
              </section>
            )}

            {elenco && elenco.amici.length > 0 && (
              <section className="scheda-account" aria-labelledby="amici-elenco">
                <h2 className="scheda-titolo" id="amici-elenco">
                  {t("amici.i_tuoi", { n: elenco.amici.length })}
                </h2>
                <ul className="ld-persone">
                  {elenco.amici.map((a, i) => (
                    <RigaPersona
                      key={a.id}
                      nome={a.nome}
                      nomeUtente={a.nomeUtente}
                      avatar={a.avatar}
                      online={a.online}
                      stato={a.sfidabile ? t("amici.puoi_sfidare") : a.online ? t("amici.online_non_sfide") : t("amici.offline")}
                      angolo={
                        <button type="button" className="ld-persona-togli" aria-label={t("amici.togli_nome", { nome: a.nome })} title={t("amici.togli")} onClick={() => setTogli(a)}>
                          {/* persona con il meno: «togli dagli amici» */}
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="9" cy="8" r="3.5" />
                            <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
                            <path d="M16 11h6" />
                          </svg>
                        </button>
                      }
                    >
                      {a.sfidabile && (
                        <button
                          type="button"
                          className="pulsante pulsante-primario pulsante-piccolo ld-sfida-persona"
                          style={{ animationDelay: `${i * 60}ms` }}
                          disabled={invio !== null}
                          onClick={() => void sfida(`a${a.id}`, (n) => sfidaAmico(a.id, n))}
                        >
                          {invio === `a${a.id}` ? t("generale.caricamento") : t("amici.sfida")}
                          <IconaSfida />
                        </button>
                      )}
                    </RigaPersona>
                  ))}
                </ul>
              </section>
            )}

            {elenco && elenco.amici.length === 0 && elenco.ricevute.length === 0 && (
              <section className="scheda-account ld-amici-vuoto">
                <p className="ld-amici-vuoto-titolo">{t("amici.vuoto_titolo")}</p>
                <ol className="ld-amici-come">
                  <li>{t("amici.come_partita")}</li>
                  <li>{t("amici.come_link")}</li>
                </ol>
              </section>
            )}

            {elenco && elenco.inviate.length > 0 && (
              <section className="scheda-account" aria-labelledby="amici-inviate">
                <h2 className="scheda-titolo" id="amici-inviate">
                  {t("amici.inviate")}
                </h2>
                <ul className="ld-persone ld-persone-piccole">
                  {elenco.inviate.map((p) => (
                    <RigaPersona key={p.id} nome={p.nome} nomeUtente={p.nomeUtente} avatar={p.avatar} stato={t("amici.in_attesa")}>
                      <button
                        type="button"
                        className="pulsante pulsante-piccolo"
                        onClick={() => {
                          void togliAmico(p.id).finally(() => void carica());
                        }}
                      >
                        {t("amici.ritira")}
                      </button>
                    </RigaPersona>
                  ))}
                </ul>
              </section>
            )}

            <button type="button" className="pulsante pulsante-secondario" onClick={() => vai(PERCORSO_DISPONIBILI)}>
              {t("home.sconosciuto")}
            </button>

            <PersoneBloccate aggiorna={bloccati} />
          </>
        )}
      </main>

      <Finestra aperta={togli !== null} titolo={t("amici.togli_titolo", { nome: togli?.nome ?? "" })} onChiudi={() => setTogli(null)}>
        <p className="ld-avviso-pericolo" role="alert">
          <span aria-hidden="true">⚠️ </span>
          {t("amici.togli_testo", { nome: togli?.nome ?? "" })}
        </p>
        <div className="azioni-riga">
          <button type="button" className="pulsante pulsante-secondario" onClick={() => setTogli(null)}>
            {t("generale.annulla")}
          </button>
          <button
            type="button"
            className="pulsante pulsante-pericolo"
            onClick={() => {
              const a = togli;
              setTogli(null);
              if (a) void togliAmico(a.id).finally(() => void carica());
            }}
          >
            {t("amici.togli")}
          </button>
        </div>
        {/* v039: bloccare senza segnalare */}
        <p className="nota">{t("amici.blocca_testo", { nome: togli?.nome ?? "" })}</p>
        <button
          type="button"
          className="pulsante-link ld-blocca"
          onClick={() => {
            const a = togli;
            setTogli(null);
            if (a)
              void bloccaPersona({ id: a.id }).finally(() => {
                setBloccati((n) => n + 1);
                void carica();
              });
          }}
        >
          {t("amici.blocca_nome", { nome: togli?.nome ?? "" })}
        </button>
      </Finestra>
    </div>
  );
}
